/**
 * Laabha — user-scoped settings.
 *
 * Source of truth: aiaccountant DB via GET/PUT /api/v1/aiaccountant/tally-settings.
 * localStorage acts as a fast read-cache so the UI never blocks on a network call.
 * On mount, Settings modal always does a GET to refresh from DB.
 */

const KEY = 'axondesktop.settings';

/** Base URL for the aiaccountant service (proxied through nginx). */
const AIACCOUNTANT_BASE = '/api/v1/aiaccountant';

export interface Settings {
  /** ngrok tunnel URL to user's local TallyPrime XML server (port 9000). */
  tally_url: string;
  /** Active company in Tally (folder-name match). Empty = first/auto. */
  company_id: string;
  /** Tally bank ledger name (e.g. "Canara Bank-209305"). Empty = auto-detect. */
  bank_ledger: string;
  /** Default Digital Employee slug for new chats. */
  default_slash: string;
  /** Optional model override for the agent (e.g., 'claude-sonnet-4-6'). */
  model_override: string;
  /** UI theme. */
  theme: 'light' | 'dark' | 'system';
}

export const DEFAULTS: Settings = {
  tally_url: '',
  company_id: '',
  bank_ledger: '',
  default_slash: '/tally-import',
  model_override: '',
  theme: 'system',
};

// ── localStorage cache (fast, synchronous) ───────────────────────────────────

export function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveSettings(s: Partial<Settings>): Settings {
  const merged = { ...loadSettings(), ...s };
  localStorage.setItem(KEY, JSON.stringify(merged));
  // Custom event so React hooks can react across tabs/components.
  window.dispatchEvent(new CustomEvent('axondesktop.settings.changed', { detail: merged }));
  return merged;
}

export function resetSettings(): Settings {
  localStorage.removeItem(KEY);
  window.dispatchEvent(new CustomEvent('axondesktop.settings.changed', { detail: DEFAULTS }));
  return { ...DEFAULTS };
}

// ── DB-backed persistence (authoritative, async) ──────────────────────────────

/**
 * Load Tally settings from the aiaccountant DB.
 * Falls back to localStorage cache if the request fails.
 */
export async function loadSettingsFromDB(): Promise<Settings> {
  try {
    const res = await fetch(`${AIACCOUNTANT_BASE}/tally-settings`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    if (!body?.success) throw new Error(body?.error?.message ?? 'Failed');
    const db = body.data as { tally_url: string; company_id: string; bank_ledger: string };
    // Merge DB tally fields on top of local cache (preserves theme / model_override)
    const merged = saveSettings({
      tally_url:   db.tally_url   ?? '',
      company_id:  db.company_id  ?? '',
      bank_ledger: db.bank_ledger ?? '',
    });
    return merged;
  } catch {
    // Network error or container down — just return cached values
    return loadSettings();
  }
}

/**
 * Persist Tally-related settings to the aiaccountant DB (and update localStorage cache).
 * Only sends the fields the DB cares about; theme/model_override stay local.
 */
export async function saveSettingsToDB(s: Partial<Settings>): Promise<Settings> {
  const merged = saveSettings(s); // update cache immediately (optimistic)
  try {
    await fetch(`${AIACCOUNTANT_BASE}/tally-settings`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tally_url:   merged.tally_url,
        company_id:  merged.company_id,
        bank_ledger: merged.bank_ledger,
      }),
    });
  } catch {
    // Fire-and-forget — local cache already updated; DB will sync on next save
  }
  return merged;
}
